import React, { useState } from 'react'
import { Shield, Lock, Trash2, ExternalLink } from 'lucide-react'

interface Props {
  onAccept: () => void
}

export default function ConsentStep({ onAccept }: Props) {
  const [agreed, setAgreed] = useState(false)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-emerald-50 px-6 py-5 border-b border-emerald-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Your Data Privacy & Security
            </h2>
            <p className="text-sm text-gray-600">
              Please read this before uploading any documents
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        <p className="text-gray-700">
          We understand that diagnostic reports contain sensitive information about your child.
          Here's exactly how we handle your data:
        </p>

        <div className="space-y-4">
          <DataPoint
            icon={<Lock className="w-5 h-5" />}
            title="Who processes your data"
            description="Your report is processed by Koru Kids using Anthropic's Claude AI. The text is sent securely to generate your briefing pack."
          />

          <DataPoint
            icon={<Shield className="w-5 h-5" />}
            title="What happens to your data"
            description="Your report text is sent to the AI once, used to generate the briefing pack, and then discarded. We do not store your data."
          />

          <DataPoint
            icon={<Trash2 className="w-5 h-5" />}
            title="No training on your data"
            description="Anthropic does not use your data to train their AI models. Your child's information remains private."
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> Since we don't store your report, if you want to keep
            the briefing pack, you'll need to download it as a PDF. You can always generate
            a new one by uploading your documents again.
          </p>
        </div>

        <a
          href="https://www.anthropic.com/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800"
        >
          View Anthropic's Privacy Policy
          <ExternalLink className="w-4 h-4" />
        </a>

        <div className="border-t border-gray-200 pt-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700">
              I understand how my data will be processed and consent to Koru Kids processing
              my child's diagnostic material solely for generating a Nanny Briefing Pack.
            </span>
          </label>
        </div>

        <button
          onClick={onAccept}
          disabled={!agreed}
          className={`
            w-full py-3 px-4 rounded-lg font-medium transition-colors
            ${agreed
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue to upload
        </button>
      </div>
    </div>
  )
}

function DataPoint({
  icon,
  title,
  description
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-700">
        {icon}
      </div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      </div>
    </div>
  )
}
